import {
  LibraryPrivacy,
  PaginatedListOfComponentTemplateDto,
} from "@growth-machine-llc/stridist-api-client";
import { useOptimisticUpdateMutation } from "../../../hooks/useOptimisticUpdateMutation";
import { useQueryClient } from "@tanstack/react-query";

import { AllOption, type LibraryPrivacyFilter } from "../../../constants";
import { COMPONENT_TEMPLATES_LIBRARY_QUERY_KEY } from "../ComponentTemplateLibrary";
import ComponentsService from "../../../services/ComponentsService";

const getPrivacyToInvalidate = (privacyType: LibraryPrivacyFilter) => {
  return [AllOption, LibraryPrivacy.TEAM, LibraryPrivacy.PRIVATE].filter(
    (t) => t !== privacyType,
  );
};

export const useToggleComponentTemplateVisibilityMutation = (
  privacy: LibraryPrivacyFilter,
  disableToastAlerts: boolean = false,
) => {
  const queryClient = useQueryClient();

  const componentTemplatePrivacyToInvalidate = getPrivacyToInvalidate(privacy);

  return useOptimisticUpdateMutation({
    queryKey: [COMPONENT_TEMPLATES_LIBRARY_QUERY_KEY, { privacy }],
    invalidateQueryOptions: {
      enabled: true,
      refetchType: "none",
    },
    disableToastAlerts,
    mutationFn: ComponentsService.toggleTeamComponentTemplatePrivacy,
    optimisticUpdater: {
      updateFn: (oldData: PaginatedListOfComponentTemplateDto, variables) => {
        const shouldRemoveExercise =
          privacy === LibraryPrivacy.TEAM && !variables.isTeamShared;

        const items = shouldRemoveExercise
          ? oldData.items.filter((ex) => ex.id !== variables.id)
          : oldData.items.map((ex) =>
              ex.id === variables.id
                ? {
                    ...ex,
                    privacy: variables.isTeamShared
                      ? LibraryPrivacy.TEAM
                      : LibraryPrivacy.PRIVATE,
                  }
                : ex,
            );

        return { ...oldData, items } as PaginatedListOfComponentTemplateDto;
      },
    },

    options: {
      onSuccess: () => {
        componentTemplatePrivacyToInvalidate.forEach((type) => {
          queryClient.invalidateQueries({
            queryKey: [
              COMPONENT_TEMPLATES_LIBRARY_QUERY_KEY,
              { privacy: type },
            ],
            exact: false,
          });
        });
      },
    },
  });
};
